/**
 * CORE-1211 — отказ, вынесенный хабом, и обрыв связи — разные вещи, и лечатся
 * они по-разному.
 *
 * В Sentry за месяц набежало 2356 событий `HubException: forbidden` на
 * `WatchChannel` от 19 аккаунтов. Столько раз человек «случайно открыть чужой
 * канал» не может: повторял клиент. Повторял потому, что у него не было способа
 * отличить «связь оборвалась» от «сервер ответил: нельзя».
 *
 * Разница принципиальная. Обрыв — состояние временное, и вся обвязка канала
 * (запасной РФ-путь, перезапуск с отступом, повторная подписка после
 * автопереподключения) написана ровно на него: пробуй ещё, рано или поздно
 * получится. Отказ в доступе — это решение сервера о правах, оно не изменится
 * ни от смены пути, ни от паузы, ни от тысячи повторов. Повтор здесь не
 * попытка, а долбёжка: сервер каждый раз честно платит запросом к базе и пишет
 * ошибку, человек всё равно видит один и тот же экран.
 *
 * Список закрытый и повторяет строки, которые бросает `ChatHub` там, где
 * решение принято окончательно: приватный канал без членства (или бан),
 * несуществующий канал, токен без идентификатора. Отказы отправки
 * (`muted`, `banned`, `moderation_blocked` и прочие из `CanPostAsync`) сюда не
 * входят намеренно — они приходят с `SendMessage`, соединения не касаются и
 * обрабатываются на своём месте.
 *
 * Совпадение по строке — не изящно, но это и есть контракт. Только текст
 * `HubException` приходит НЕ дословно (CORE-1468): ASP.NET Core пишет
 * «An unexpected error occurred invoking 'WatchChannel' on the server.
 * HubException: forbidden» — замерено на хабе стенда 22.09.2026. Голым токеном
 * его отдавали только моки в тестах, поэтому сравнение со всей строкой не
 * срабатывало никогда: отказ читался как вставший путь, и клиент шёл вторым
 * заходом через запасной адрес. На стенде запасной адрес — dev-релей, он
 * отвечает на чужой токен 401, и человек, которого просто нет в круге, видел
 * «Войдите заново». Любую другую серверную ошибку SignalR сводит к той же фразе
 * без хвоста `HubException: …`, так что перепутать отказ с поломкой нельзя.
 */
const HUB_REFUSALS: ReadonlySet<string> = new Set([
  // ChatHub.WatchChannel — канал приватный, а членства нет или оно забанено.
  'forbidden',
  // ChatHub.WatchChannel — такого канала нет вовсе.
  'channel not found',
  // ChatHub.UserId — токен есть, идентификатора в нём нет.
  'unauthenticated',
]);

/** Хвост, которым SignalR передаёт текст `HubException`. */
const HUB_EXCEPTION_TAIL = /\bHubException: (.*)$/;

/** Текст, брошенный `ChatHub`, без обёртки SignalR. */
function hubExceptionText(message: string): string {
  const tail = HUB_EXCEPTION_TAIL.exec(message);
  return (tail ? tail[1] : message).trim();
}

/** Хаб отказал по существу — повторять этот вызов бессмысленно. */
export function isHubRefusal(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return HUB_REFUSALS.has(hubExceptionText(message));
}
